import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { Tabs } from '~/components';
import { formatSubspecialties } from '~/features/market/listings/subspecialties';
import { MarketContentPanel, MarketStatusPill } from '~/features/shared/market-content';
import { SearchFit } from '~/features/shared/market-content/SearchFit';
import type { SearchFitResult } from '~/features/shared/market-content/useSearchFit';
import { buildNewMsaasListingUrl } from '~/routing';
import { useMsaasListingSearchRef } from '../../marketSelectors';
import type { CompareListingOption } from '../api';
import { compareListingFor } from '../compareListings';
import '../listingPresentation.css';
import { useListingProviderCategories } from '../ProviderCategoriesContext';
import { useSearchSide } from '../searchSide';
import { selectFitQuotaActive, selectSearchListing, selectSearchTabIndex } from '../selectors';
import { ActionButtons } from './ActionButtons';
import { Compensation } from './Compensation';
import { ContractOverview } from './ContractOverview';
import { ContractTerms } from './ContractTerms';
import { OfferingFitDetail } from './OfferingFit';
import { OfferingCoverage, OfferingRoster } from './OfferingSellerDetails';

const TAGS = CT.listingSearch;

export const DetailPanel = ({
  fit,
  compareListings = [],
}: {
  fit?: SearchFitResult;
  // Buying tab only: the buyer's own listings a fit can be compared against.
  compareListings?: CompareListingOption[];
}) => {
  const { msaasListingsSearchRef } = useMsaasListingSearchRef();
  const side = useSearchSide();
  const listing = useSelector(msaasListingsSearchRef, selectSearchListing);
  const tabIndex = useSelector(msaasListingsSearchRef, selectSearchTabIndex);
  const quotaActive = useSelector(msaasListingsSearchRef, selectFitQuotaActive);
  const compare = compareListingFor(listing?.fit?.compareListingId, compareListings);
  const categories = useListingProviderCategories()?.categories ?? [];
  const providerCategoryName =
    categories.find((category) => category.id === listing?.providerCategoryId)
      ?.providerCategoryCode ||
    (listing?.providerCategoryId ? `Category ${listing.providerCategoryId}` : '—');

  const subspecialties = listing ? formatSubspecialties(listing) : null;

  if (!msaasListingsSearchRef) return null;
  const DASHBOARD_TABS = [
    {
      name: 'Contract Overview ',
      current: true,
      cyTag: TAGS.detailPanel.contractOverviewTab,
    },
    {
      name: 'Compensation',
      current: false,
      cyTag: TAGS.detailPanel.compensationTab,
    },
    {
      name: 'Contract Terms',
      current: false,
      cyTag: TAGS.detailPanel.contractTermsTab,
    },
  ];
  const handleTabChange = (index: number) => {
    msaasListingsSearchRef.send({ type: 'ON_SET_TAB_INDEX', payload: { index } });
  };

  return (
    // [MCV-1558] ActionButtons is a sibling of MarketContentPanel here, not
    // nested inside it: it now positions itself with `sticky bottom-0` (see
    // ActionButtons.tsx), which sticks to the bottom of the nearest
    // scrolling ancestor -- the `overflow-y-auto` detail column in
    // ListingSearch.tsx. Nesting it inside MarketContentPanel's padded body
    // div would leave that padding as a gap below the sticky bar instead of
    // pinning it flush to the column's bottom edge.
    <>
      <MarketContentPanel className="b2b-listing-presentation" dataCy="b2b-listing-detail">
        <div className="mb-6">
          <div className="mb-6 text-left">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <h2
                className="min-w-0 text-2xl font-bold leading-snug text-content-primary [overflow-wrap:anywhere]"
                data-cy={TAGS.contractOverview.listingTitle}
              >
                {listing?.listingTitle}
              </h2>
              <MarketStatusPill
                status={listing?.activeFlag ? 'active' : 'inactive'}
                label={listing?.activeFlag ? 'Active Listing' : 'Inactive Listing'}
                cyTag={TAGS.contractOverview.listingStatus}
              />
            </div>
            {side === 'OFFERING'
              ? listing && (
                  <OfferingFitDetail
                    listing={listing}
                    compareTitle={compare?.listingTitle}
                    compareFtes={compare?.detail?.numOfFtes}
                    canCompare={compareListings.length > 0}
                    quotaActive={quotaActive}
                    postNeedHref={buildNewMsaasListingUrl('buying')}
                    onCheckFit={() =>
                      msaasListingsSearchRef.send({
                        type: 'ON_CHECK_FIT',
                        payload: { listingId: listing.id },
                      })
                    }
                  />
                )
              : fit &&
                listing && (
                  <SearchFit
                    kind="b2b"
                    summary={fit.scores[listing.id]}
                    loading={fit.loading}
                    error={fit.error}
                    onRetry={fit.retry}
                    details
                  />
                )}
            <dl
              className="mb-4 grid grid-cols-[repeat(auto-fit,minmax(min(100%,220px),1fr))] gap-4"
              data-cy={TAGS.contractOverview.employerInfo}
            >
              <div>
                <dt className="text-sm font-semibold leading-5 text-content-secondary">
                  Provider type
                </dt>
                <dd className="mt-1 text-[1rem] leading-6 [overflow-wrap:anywhere]">
                  {providerCategoryName || '—'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-semibold leading-5 text-content-secondary">
                  Specialty
                </dt>
                <dd className="mt-1 text-[1rem] leading-6 [overflow-wrap:anywhere]">
                  {listing?.specialtyName || '—'}
                </dd>
              </div>
              {subspecialties && (
                <div>
                  <dt className="text-sm font-semibold leading-5 text-content-secondary">
                    Subspecialties
                  </dt>
                  <dd
                    className="mt-1 text-[1rem] leading-6 [overflow-wrap:anywhere]"
                    data-cy={TAGS.contractOverview.subspecialties}
                  >
                    {subspecialties}
                  </dd>
                </div>
              )}
            </dl>
            <p className="mb-2 text-sm leading-6 text-content-secondary [overflow-wrap:anywhere]">
              {[
                listing?.employer?.employerInfo?.employerType,
                listing?.employer?.employerInfo?.ownershipStructure,
              ]
                .filter(Boolean)
                .join(' • ')}
            </p>
            <p className="text-sm leading-6 text-content-secondary">
              Posted At:{' '}
              {new Date(listing?.createdAt || '').toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                timeZone: 'UTC',
              })}
            </p>
          </div>
          {side === 'OFFERING' && listing && (
            <>
              <OfferingCoverage listing={listing} />
              <OfferingRoster listing={listing} />
            </>
          )}
          <Tabs tabs={DASHBOARD_TABS} handleTabChange={handleTabChange} activeTab={tabIndex ?? 0} />
          {(tabIndex ?? 0) === 0 && <ContractOverview />}
          {tabIndex === 1 && <Compensation />}
          {tabIndex === 2 && <ContractTerms key={listing?.id} />}
        </div>
      </MarketContentPanel>
      <ActionButtons />
    </>
  );
};
